import { useQuery } from "@tanstack/react-query";
import { fetchVimeoThumbnail } from "@/lib/vimeo";

/** A video's thumbnail does not change from one minute to the next. */
const THUMBNAIL_STALE_MS = 6 * 60 * 60 * 1000;

/**
 * The thumbnail Vimeo shows for a video, looked up once per visit however
 * many cards ask for it.
 *
 * Pass null to not ask at all. That is how a card that already has a working
 * image avoids a request to a third party it does not need.
 *
 * `data` is undefined while loading, after a failed lookup, and when asked
 * nothing; the string only when there is a picture to show. Callers treat all
 * three the same way (keep the branded panel), so they are not told apart.
 */
export const useVideoThumbnail = (lookupUrl: string | null) =>
  useQuery({
    queryKey: ["video-thumbnail", lookupUrl],
    queryFn: ({ signal }) => fetchVimeoThumbnail(lookupUrl as string, signal),
    enabled: !!lookupUrl,
    staleTime: THUMBNAIL_STALE_MS,
    // One more try for a blip, then give up quietly: the card already looks
    // right without it, so there is nothing worth hammering Vimeo for.
    retry: 1,
    retryDelay: 1500,
    refetchOnWindowFocus: false,
  });
